import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { FormControlLabel, Switch, InputAdornment } from '@mui/material';
import { CheckPermission } from '../CheckPermission';
import { MHMTextField } from '../MHM/MHMTextField';
import { MHMTypography, DisclaimerText, ErrorText } from '../MHM/MHMTypography';
import { useTranslation } from '../../../contexts/TranslationContext';
import { canManageDevicesSelector } from '../../../models/selectors/permission_selectors';
import { changeROCAtom } from '../../../models/atoms/company_atoms';

type Props = {
  setROCThresholdError(isError: boolean): void;
};

export function ROCSettings({ setROCThresholdError }: Props) {
  const { t, distance } = useTranslation();
  const canManageDevices = useRecoilValue(canManageDevicesSelector);
  const [rocSettings, setROCSettings] = useRecoilState(changeROCAtom);
  const [rocSettingsError, setROCSettingsError] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0);

  function toggleROCMode(e: React.ChangeEvent<HTMLInputElement>) {
    if (!canManageDevices) {
      return;
    }

    setROCSettings(prev =>
      prev
        ? {
            ...prev,
            roc_mode_on: e.target.checked,
            change_roc: true,
          }
        : prev,
    );
  }

  function handleROCThresholdChange(e: React.ChangeEvent<HTMLInputElement>) {
    setROCSettingsError('');
    const value = distance.from(e.target.value);
    if (!/^[0-9.,]+$/.test(e.target.value)) {
      setROCSettingsError('Please provide a valid number!');
      setROCThresholdError(true);
      return;
    } else if (value < 50 || value > 250) {
      setROCSettingsError(
        'Not in the allowable range of 50-250 millimeters per hour!',
      );
      setROCThresholdError(true);
      return;
    }

    setROCThresholdError(false);
    setROCSettings(prev =>
      prev
        ? {
            ...prev,
            roc_threshold: value,
            change_roc: true,
          }
        : prev,
    );
  }

  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [distance]);

  if (!rocSettings) {
    return null;
  }

  return (
    <>
      <div>
        <CheckPermission hasPermission={canManageDevices}>
          <FormControlLabel
            control={
              <Switch
                checked={rocSettings.roc_mode_on}
                disabled={!canManageDevices}
                onChange={toggleROCMode}
              />
            }
            label={
              <MHMTypography medium>{t('Rate of Change (ROC)')}</MHMTypography>
            }
          />
        </CheckPermission>
        <DisclaimerText fontSize="small">
          {t(
            'The device calculates the rate of change (ROC). High Warning Mode overrides Weather Mode (if engaged).',
          )}
        </DisclaimerText>
      </div>

      {rocSettings.roc_mode_on && (
        <CheckPermission hasPermission={canManageDevices}>
          <MHMTextField
            key={forceUpdate}
            label={t('ROC Threshold')}
            defaultValue={distance
              .from_metric(rocSettings.roc_threshold)
              .toFixed(1)}
            disabled={!canManageDevices}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">{t('mm/hour')}</InputAdornment>
                ),
              },
            }}
            size="small"
            onChange={handleROCThresholdChange}
            error={Boolean(rocSettingsError)}
            helperText={
              <ErrorText component="span">{t(rocSettingsError)}</ErrorText>
            }
            sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}
          />
        </CheckPermission>
      )}
    </>
  );
}
